import { FormFields, useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { usePricingEditorDomainContext } from 'common/drawers/PricingEditor/communication'
import {
  PricingEditorReducerState,
  BillingFrequency,
  PriceEditorTier,
  PriceEditorPercentageTier
} from 'common/drawers/PricingEditor/domain'
import { defaultAvailableFrequenciesOptions } from 'common/drawers/PricingEditor/domain/pricingEditor.constants'
import { useMemo } from 'react'
import {
  CommonFields,
  useCommonFieldConfig
} from '../common/useCommonFieldConfig'
import { useSyncFormValidity } from '../common/useSyncFormValidity'
import { Currency } from '@sequencehq/api'
import { useUsageMetricParameterFieldConfig } from '../common/UsageMetrics/useUsageMetricParameterFieldConfig'
import {
  percentageTiersValidator,
  tiersValidator
} from 'common/drawers/PricingEditor/utils/validators/tiers.validators'
import usageCalculationPeriodMapper from 'common/drawers/PricingEditor/utils/usageCalculationPeriodMapper.ts'
import { UsageCalculationPeriodDropdownValue } from 'common/drawers/PricingEditor/view/editors/GraduatedPrice/UsageCalculationPeriodDropdown/UsageCalculationPeriodDropdown.tsx'
import {
  availableUsageCalculationModes,
  availableUsageCalculationPeriodsByBillingFrequency,
  availableUsageTierTypes
} from 'common/drawers/PricingEditor/view/editors/GraduatedPrice/GraduatedPriceForm.constants.ts'
import { commonPriceDetails } from '../common/ReadOnlyDetails/readOnlyDetails.utils'
import { useUsageMetrics } from '../../../communication/external/useUsageMetrics'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { getBillingFrequencyMultiplier } from 'common/drawers/PricingEditor/utils/getBillingFrequencyMultiplier'
import { floatSafeMultiply, enforceMinimumPrecision } from '@sequencehq/utils'
import { usageMetricIdValidator } from 'common/drawers/PricingEditor/utils/validators/usageMetricId.validators'
import { dequal } from 'dequal'
import { pick } from 'lodash/fp'

type GraduatedPriceFields = Omit<
  PricingEditorReducerState['data']['pricingEditorData']['GRADUATED'],
  'usageCalculationPeriod'
> & {
  usageCalculationPeriod: UsageCalculationPeriodDropdownValue
}

type GraduatedPriceFormFieldConfig = Omit<
  FormFields<CommonFields>,
  'pricingModel' | 'currency'
> & {
  currency: FormFields<CommonFields>['currency'] & {
    hidden: boolean
  }
} & Omit<FormFields<GraduatedPriceFields>, 'usageCalculationPeriod'> & {
    usageCalculationPeriod: FormFields<GraduatedPriceFields>['usageCalculationPeriod'] & {
      hidden: boolean
    }
  }

type UseGraduatedPriceForm = () => {
  fieldsConfig: GraduatedPriceFormFieldConfig
  currency: Currency
  priceDetails: Array<{ value: string; label: string }>
}

const adjustTierFlatFlatFee =
  <T extends { flatFee: string }>(ratio: number) =>
  (tier: T): T => {
    const flatFeePrecision = tier.flatFee.split('.')[1]?.length ?? 0
    const flatFeeTargetPrecision = Math.max(flatFeePrecision, 2)

    return {
      ...tier,
      flatFee: enforceMinimumPrecision(2)(
        floatSafeMultiply(+tier.flatFee, ratio, flatFeeTargetPrecision)
      )
    }
  }

const adjustGraduatedPrice = (params: {
  ratio: number
  tiers: PriceEditorTier[]
  percentageTiers: PriceEditorPercentageTier[]
}) => {
  /**
   * We format both of them on a change, since we don't blank them out
   * on a calculation change either, since we won't want to lose potentially
   * large amounts of tiers by accident! Therefore, keeping both tier types
   * in sync makes sense.
   */
  return {
    tiers: params.tiers.map(adjustTierFlatFlatFee(params.ratio)),
    percentageTiers: params.percentageTiers.map(
      adjustTierFlatFlatFee(params.ratio)
    )
  }
}

export const useGraduatedPriceForm: UseGraduatedPriceForm = () => {
  const pricingEditorContext = usePricingEditorDomainContext()
  const flags = useFeatureFlags()

  const commonFields = useCommonFieldConfig<
    GraduatedPriceFields & CommonFields
  >()
  const parameterFields = useUsageMetricParameterFieldConfig<
    GraduatedPriceFields & CommonFields
  >(
    pricingEditorContext.queries.rawData.data.pricingEditorData.GRADUATED
      .usageMetricId,
    () => false
  )

  /**
   * Allow for the on demand billing frequency if the flag is enabled, or the value has already been set (i.e. the flag is now disabled,
   * but a price was set using it). However, when the flag is disabled this will act as trap door state, and will cause the value to
   * disappear from selection once changed.
   */
  const onDemandFrequencyAvailable =
    pricingEditorContext.queries.rawData.data.pricingEditorData.GRADUATED
      .billingFrequency === 'ON_DEMAND' || flags.useOnDemandBillingFrequency

  const perEventFrequencyAvailable =
    pricingEditorContext.queries.rawData.data.pricingEditorData.GRADUATED
      .billingFrequency === 'PER_EVENT' || flags.usePerEventBilling

  const { usageMetrics } = useUsageMetrics()
  const validateUsageMetricId = usageMetricIdValidator(usageMetrics)

  /**
   * The base form initial value. The form value will not be updated
   * if any of these values change outside of the form.
   */
  const contextUsageCalculationPeriod =
    usageCalculationPeriodMapper.fromApiPricePropertiesToUsageCalculationPeriodDropdown(
      {
        usageCalculationMode:
          pricingEditorContext.queries.rawData.data.pricingEditorData.GRADUATED
            .usageCalculationMode,
        usageCalculationPeriod:
          pricingEditorContext.queries.rawData.data.pricingEditorData.GRADUATED
            .usageCalculationPeriod
      },
      pricingEditorContext.queries.rawData.data.pricingEditorData.GRADUATED
        .billingFrequency
    )

  const { fields, queries, mutators } = useForm({
    value: {
      ...pricingEditorContext.queries.rawData.data.pricingEditorData.common,
      ...pricingEditorContext.queries.rawData.data.pricingEditorData.GRADUATED,
      usageCalculationPeriod: contextUsageCalculationPeriod
    },
    reinitializeOnValueChange: (params: {
      newData: GraduatedPriceFields & CommonFields
      prevData: (GraduatedPriceFields & CommonFields) | undefined
    }) => {
      const propertiesToCompare = pick([
        'productId',
        'listPriceId',
        'usageMetricId',
        'usageCalculationPeriod'
      ])
      return !dequal(
        propertiesToCompare(params.newData),
        propertiesToCompare(params.prevData)
      )
    },
    fieldConfiguration: [
      ...commonFields.fields,
      {
        property: 'billingFrequency',
        options: [
          ...defaultAvailableFrequenciesOptions,
          ...(onDemandFrequencyAvailable
            ? [
                {
                  value: 'ON_DEMAND',
                  label: 'On demand'
                }
              ]
            : []),
          ...(perEventFrequencyAvailable
            ? [
                {
                  value: 'PER_EVENT',
                  label: 'Per event'
                }
              ]
            : [])
        ],
        validation: [required]
      },
      {
        property: 'usageMetricId',
        validation: [required, validateUsageMetricId]
      },
      {
        property: 'tiers',
        disabled: ctx => ctx.formData.usageTierType !== 'FIXED',
        validation: [required, tiersValidator]
      },
      {
        property: 'percentageTiers',
        disabled: ctx => ctx.formData.usageTierType !== 'PERCENTAGE',
        validation: [required, percentageTiersValidator]
      },
      {
        property: 'usageTierType',
        validation: [required],
        options: availableUsageTierTypes
      },
      {
        property: 'usageCalculationMode',
        validation: [],
        options: availableUsageCalculationModes
      },
      {
        property: 'usageCalculationPeriod',
        validation: []
      },
      ...parameterFields.fields
    ],
    showValidationErrors:
      pricingEditorContext.queries.rawData.editor.showValidationErrors,
    disabled:
      !pricingEditorContext.queries.availableFeatures.common.form.available
        .enabled,
    onChange: newData => {
      const mappedCalculationStructure =
        usageCalculationPeriodMapper.fromUsageCalculationPeriodDropdownToApiPriceProperties(
          newData.usageCalculationPeriod,
          newData.billingFrequency
        )

      pricingEditorContext.mutators.common.updatePricingEditorData({
        common: {
          name: newData.name,
          currency: newData.currency
        },
        GRADUATED: {
          billingFrequency: newData.billingFrequency,
          usageMetricId: newData.usageMetricId,
          tiers: newData.tiers,
          percentageTiers: newData.percentageTiers,
          usageTierType: newData.usageTierType,
          usageCalculationMode: mappedCalculationStructure.usageCalculationMode,
          usageCalculationPeriod:
            mappedCalculationStructure.usageCalculationPeriod,
          parameters: newData.parameters
        }
      })
    }
  })

  /**
   * A usage metric's parameters come from the metric, so their property names
   * aren't known up front. They all render in the additional fields section.
   */
  const parameterFieldNames = parameterFields.fields.map(
    field => field.property
  )

  useSyncFormValidity({
    isFormValid: queries.isValid,
    formValidationResults: queries.validationResults,
    additionalFields: [
      'billingFrequency',
      'usageMetricId',
      'usageCalculationPeriod',
      ...parameterFieldNames
    ]
  })

  const enhancedFields = useMemo(
    () => ({
      ...fields,
      currency: {
        ...fields.currency,
        hidden: Boolean(
          pricingEditorContext.queries.rawData.configuration.currency
        )
      },
      billingFrequency: {
        ...fields.billingFrequency,
        /**
         * Adjust the tier prices on a frequency change.
         */
        onChange: (newBillingFrequency: BillingFrequency) => {
          const originalBillingFrequency = queries.formData.billingFrequency
          const ratio = getBillingFrequencyMultiplier({
            fromFrequency: originalBillingFrequency,
            toFrequency: newBillingFrequency
          })
          mutators.updateFormData({
            billingFrequency: newBillingFrequency,
            ...adjustGraduatedPrice({
              ratio,
              tiers: queries.formData.tiers,
              percentageTiers: queries.formData.percentageTiers
            })
          })
        }
      },
      usageCalculationPeriod: {
        ...fields.usageCalculationPeriod,
        // Per-event prices invoice each event independently — there is no
        // usage window to configure, so hide the dropdown entirely.
        hidden: queries.formData.billingFrequency === 'PER_EVENT',
        /**
         * Add the (default) label to the default option, which varies based on the selected
         * billing frequency. On demand has a default of cumulative, and everything else
         * has a default of billing period.
         */
        options: availableUsageCalculationPeriodsByBillingFrequency[
          queries.formData.billingFrequency
        ].map(opt => {
          if (
            (opt.value === 'CUMULATIVE_USAGE' &&
              queries.formData.billingFrequency === 'ON_DEMAND') ||
            opt.value === 'BILLING_PERIOD'
          ) {
            return {
              ...opt,
              label: `${opt.label} (default)`
            }
          }

          return opt
        })
      }
    }),
    [
      fields,
      pricingEditorContext.queries.rawData.configuration.currency,
      queries.formData.billingFrequency,
      queries.formData.tiers,
      queries.formData.percentageTiers,
      mutators
    ]
  )

  const priceDetails = useMemo(
    () => [
      ...commonPriceDetails({
        taxCategoryName:
          pricingEditorContext.queries.rawData.data.product.taxCategoryName,
        revenueRecognitionMethod:
          pricingEditorContext.queries.rawData.data.product
            .revenueRecognitionMethod,
        pricingModel: 'GRADUATED',
        billingFrequency: queries.formData.billingFrequency,
        billingType: queries.formData.billingType
      }),
      {
        label: 'Usage metric',
        value:
          usageMetrics.find(
            metric => metric.id === queries.formData.usageMetricId
          )?.label ?? 'Not selected'
      },
      {
        label: 'Type',
        value:
          fields.usageTierType.options.find(
            opt => opt.value === queries.formData.usageTierType
          )?.label ?? 'Not selected'
      },
      {
        label: 'Usage calculation period',
        value:
          fields.usageCalculationMode.options.find(
            opt => opt.value === queries.formData.usageCalculationMode
          )?.label ?? 'Not selected'
      }
    ],
    [
      fields.usageCalculationMode.options,
      usageMetrics,
      fields.usageTierType.options,
      queries.formData.billingFrequency,
      queries.formData.billingType,
      queries.formData.usageCalculationMode,
      queries.formData.usageMetricId,
      queries.formData.usageTierType,
      pricingEditorContext.queries.rawData.data.product.taxCategoryName,
      pricingEditorContext.queries.rawData.data.product.revenueRecognitionMethod
    ]
  )

  return {
    fieldsConfig: enhancedFields,
    currency:
      pricingEditorContext.queries.rawData.data.pricingEditorData.common
        .currency,
    priceDetails
  }
}
